import { useEffect } from "react";
// eslint-disable-next-line import/no-unresolved
import "quill-mention/autoregister";
import { useTranslation } from "react-i18next";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";
import "quill-mention/dist/quill.mention.css";

import { sanitizeHTML } from "../../utils/htmlSanitizer";
import { mobileQuilllCss, quillCss } from "./RichTextEditor.styles";

export interface IMentionItem {
  id: string;
  value: string;
  [key: string]: string | undefined;
}

export interface IMentionConfig {
  source: (
    searchTerm: string,
    renderList: (matches: IMentionItem[], searchTerm: string) => void
  ) => void;
  renderItem?: (item: IMentionItem, searchTerm: string) => string | HTMLElement;
}

type RichTextEditorProps = {
  placeholderTxt?: string;
  height?: number;
  value?: string;
  readOnly?: boolean;
  hideToolbar?: boolean;
  onValueChange: (value: string) => void;
  onFocus?: () => void;
  onBlur?: () => void;
  mobile?: boolean;
  mentionConfig?: IMentionConfig;
};

export function RichTextEditor({
  placeholderTxt,
  value,
  height = 300,
  hideToolbar = false,
  readOnly = false,
  onValueChange,
  mobile,
  onFocus,
  onBlur,
  mentionConfig,
}: RichTextEditorProps) {
  const { t } = useTranslation();

  const toolbar =
    readOnly || hideToolbar
      ? false
      : [["bold", "strike", "underline", "italic", "image", "link"]];
  const placeholder = readOnly
    ? ""
    : placeholderTxt ?? t("common_enterTextHere");

  const modules: Record<string, unknown> = { toolbar };
  if (mentionConfig && !readOnly) {
    modules.mention = {
      allowedChars: /^[A-Za-z0-9._@-]*$/,
      mentionDenotationChars: ["@"],
      isolateCharacter: true,
      spaceAfterInsert: true,
      source: mentionConfig.source,
      renderItem: mentionConfig.renderItem,
    };
  }

  const toolBarHeight = readOnly ? 0 : 42;

  const formats = [
    "bold",
    "strike",
    "underline",
    "italic",
    "image",
    "link",
    "mention",
  ];

  const { quill, quillRef, Quill } = useQuill({
    modules,
    placeholder,
    readOnly,
    formats,
  });

  useEffect(() => {
    if (quill) {
      // quill-mention registers the MentionBlot (Embed) but does NOT register
      // a clipboard matcher for span.mention. So when we paste saved HTML
      // back into the editor (Edit modal), Quill parses the mention as a
      // generic <span> + child spans instead of an atomic embed — the caret
      // can sit inside it and Backspace edits the inner text instead of
      // removing the tag. Register a matcher that converts span.mention back
      // into an embed delta so the blot is re-created as atomic on paste.
      quill.clipboard.addMatcher("SPAN.mention", (node: Node) => {
        const el = node as HTMLElement;
        const Delta = (quill.constructor as any).import("delta");
        const data: Record<string, string | undefined> = {
          id: el.dataset.id,
          value: el.dataset.value,
          denotationChar: el.dataset.denotationChar ?? "@",
        };
        if (el.dataset.index !== undefined) {
          data.index = el.dataset.index;
        }
        return new Delta().insert({ mention: data });
      });

      // Sanitize HTML output before passing to parent component
      quill.on("text-change", () => {
        const html = quill.root.innerHTML;
        // Sanitize the HTML content to prevent XSS
        const sanitizedHtml = sanitizeHTML(html);
        onValueChange(sanitizedHtml);
      });

      quill.on("selection-change", (range: any, oldRange: any) => {
        if (range === null && oldRange !== null) {
          if (onBlur) {
            onBlur();
          }
        } else if (range !== null && oldRange === null) {
          if (onFocus) {
            onFocus();
          }
        }
      });

      // SECURITY FIX: Sanitize incoming HTML before setting it
      // This prevents stored XSS attacks when displaying saved content
      if (value) {
        const sanitizedValue = sanitizeHTML(value);
        // Use clipboard API instead of direct innerHTML to let Quill parse safely
        quill.clipboard.dangerouslyPasteHTML(0, sanitizedValue);
      } else {
        quill.setText("");
      }
    }
  }, [quill]);

  if (Quill && !quill) {
    // Add https to link if https is not present
    const Link = Quill.import("formats/link");
    Link.sanitize = function sanitize(url: string) {
      // quill by default creates relative links if scheme is missing.
      if (!url.startsWith("http://") && !url.startsWith("https://")) {
        return `http://${url}`;
      }
      return url;
    };
    Quill.register(Link, true);
  }

  return (
    <div style={{ width: "100%" }}>
      <div
        style={{
          height,
          fontSize: mobile ? "18px" : "14px",
        }}
        // className={mobile ? mobileQuilllCss() : quillCss()}
        className={mobile ? mobileQuilllCss() : quillCss()}
      >
        <div
          style={{
            width: "calc(100% - 40)",
            height: height - toolBarHeight,
            backgroundColor: "rgba(233, 230, 244, 0.16)",
            borderRadius: "6px",
            marginTop: "20px",
            border: "1px solid #E6E6E6",
            color: "rgba(33, 33, 33, 0.50)",
          }}
          ref={quillRef}
        />
      </div>
    </div>
  );
}
